import {
  type AdInputField,
  type AdInputLookup,
  type Brand,
  type EnrichedAdInputField,
  getAt,
  type ItemResponse,
  type ValidationRule,
} from '@finn-no/mobility-item-insertion-shared';
import { type Locale, useFormatNumber } from '@finn-no/number-format';
import { i18n } from '@lingui/core';
import type { RegisterOptions } from 'react-hook-form';
import type { Condition, WidgetModel, WidgetModelArray } from '../types/widget.js';
import { getHuckConfig } from './utils/getHuckConfig.js';

const isBooleanEnum = (values: { id: number; code: string; label: string }[]) => {
  if (values.length !== 2) return false;
  const codes = values.map((value) => value.code);
  return codes.includes('YES') && codes.includes('NO');
};

const resolveDefaultBooleanEnumValue = (values: { id: number; code: string; label: string }[]) => {
  const noValue = values.find((value) => value.code === 'NO');
  if (!noValue) {
    throw new Error('Expected to find NO value in boolean enum');
  }
  return noValue.id;
};

const defaultFallback = (field?: AdInputField) => {
  if (!field) return undefined;

  switch (field.type) {
    case 'BOOLEAN':
      return false;
    case 'ENUM':
      if (isBooleanEnum(field.values) && field.mandatory) {
        return resolveDefaultBooleanEnumValue(field.values);
      }
      if (field['multi-value']) {
        return [];
      }
      return undefined;
    case 'NUMBER':
      return undefined;
    case 'STRING':
      if (field['multi-value']) {
        return [];
      }
      return '';
    case 'MANAGED':
      return undefined;
    default:
      return '';
  }
};

export const getInitialValues = ({
  adInputLookup,
  item,
  parent = '',
  brand = 'finn',
  widgetModel,
}: {
  adInputLookup?: AdInputLookup;
  item?: ItemResponse;
  parent?: string;
  brand?: Brand;
  widgetModel?: WidgetModelArray;
}) => {
  return Object.keys(adInputLookup ?? {}).reduce(
    (acc, key) => {
      const adInputField = adInputLookup?.[key];
      const dotPath = [parent, key].filter((p) => p.length).join('.');

      // Fields like image needs to default to an empty array of the value on the item
      if (adInputField?.type === 'COMPLEX' && adInputField['multi-value']) {
        acc[key] = item ? (getAt(item.data, dotPath) ?? []) : [];
        return acc;
      }

      // COMPLEX field that are not multi-value are groups of fields
      if (adInputField?.type === 'COMPLEX') {
        acc[key] = getInitialValues({
          adInputLookup: adInputField.children,
          item,
          parent: dotPath,
          brand,
        });
        return acc;
      }

      // Use dot-notation path to get value from item
      const itemValue = item ? getAt(item.data, dotPath) : undefined;

      if (adInputField && 'values' in adInputField && itemValue && Array.isArray(itemValue) && key === 'equipment') {
        const enumValues = adInputField.values ?? [];
        const equipmentValues = itemValue as string[];

        const isUsingDavsIds = equipmentValues.some(
          (value) => !enumValues.some((enumValue) => enumValue.code === value),
        );

        if (isUsingDavsIds) return acc;

        acc[key] = equipmentValues.map((value) => {
          const matchedEnum = enumValues.find((enumValue) => enumValue.code === value);
          return matchedEnum ? `${matchedEnum.id}` : value;
        });

        return acc;
      }

      const defaultValue = widgetModel?.find((w) => w.name === dotPath)?.defaultValue;

      /**
       * This logic is used to convert enum values to davs ids. It should gracefully handle
       * when the fields are converted to managed fields
       *
       * TODO: Remove logic when all fields in all models are managed upstream
       */
      const fieldsToInclude = ['car_location', 'category', 'trade_type'];
      if (adInputField && 'values' in adInputField && fieldsToInclude.includes(dotPath)) {
        const enumValues = adInputField.values ?? [];

        /**
         * Will only be a value if itemValue matches one of the enum codes, the ID in davs and the enum field
         * are guaranteed to be the same
         */
        const davsId = enumValues.find((value) => value.code === itemValue)?.id;

        if (!davsId) {
          if (defaultValue) {
            acc[key] = defaultValue;
          }

          return acc;
        }

        acc[key] = davsId;
        return acc;
      }

      acc[key] = itemValue ?? defaultValue ?? defaultFallback(adInputField);

      /**
       * These fields should not be set to "" as that would cause a validation error in
       * item-management-server
       */
      if ((key === 'chassis_number' || key === 'model_and_make') && !acc[key]) {
        acc[key] = undefined;
      }

      /**
       * Handle default country address field based on brand
       */
      if (dotPath === 'address.country' && !itemValue) {
        const defaultCountryByBrandMap: Record<Brand, string> = {
          dba: 'DK',
          finn: 'NO',
          blocket: 'SE',
          bilbasen: 'DK',
        };

        acc[key] = defaultCountryByBrandMap[brand];
      }

      /**
       * Set default value of model and make to undefined if not set
       */
      if (key === 'model_and_make' && !itemValue) acc[key] = undefined;

      return acc;
    },
    {} as Record<string, unknown>,
  );
};

export const convertValidationRulesToRHFRules = (rules: ValidationRule[], dotNotationName?: string) => {
  const config = getHuckConfig();
  // biome-ignore lint/correctness/useHookAtTopLevel: not a react hook, but a utility function
  const toFormattedNumber = useFormatNumber(config.locale as Locale);

  const result: Pick<RegisterOptions, 'max' | 'min' | 'maxLength' | 'minLength' | 'required' | 'validate'> = {};

  for (const rule of rules) {
    switch (rule.type) {
      case 'MANDATORY':
        result.required = i18n.t({
          id: 'mobility-item-insertion.validation.required',
          message: 'This field is required',
          comment: 'Validation message indicating that the field is mandatory',
        });
        break;
      case 'MIN_LENGTH':
        result.minLength = {
          value: rule.value,
          message: i18n.t({
            id: 'mobility-item-insertion.validation.minLength',
            message: 'Must be {value} characters or more',
            values: { value: rule.value },
            comment:
              'Validation message indicating that the input must be longer than or equal to the specified minimum length',
          }),
        };
        break;
      case 'MAX_LENGTH':
        result.maxLength = {
          value: rule.value,
          message: i18n.t({
            id: 'mobility-item-insertion.validation.maxLength',
            message: 'Must be {value} characters or less',
            values: { value: rule.value },
            comment:
              'Validation message indicating that the input must be shorter than or equal to the specified maximum length',
          }),
        };
        break;
      case 'MIN_VALUE':
        result.min = {
          value: rule.value,
          message: i18n.t({
            id: 'mobility-item-insertion.validation.minValue',
            message: 'Value must be larger than or equal to {value}',
            values: {
              value: dotNotationName === 'price.net' ? toFormattedNumber(rule.value) : rule.value,
            },
            comment:
              'Validation message indicating that the value must be larger than or equal to the specified minimum value',
          }),
        };
        break;
      case 'MAX_VALUE':
        result.max = {
          value: rule.value,
          message: i18n.t({
            id: 'mobility-item-insertion.validation.maxValue',
            message: 'Value must be less than or equal to {value}',
            values: {
              value: dotNotationName === 'price.net' ? toFormattedNumber(rule.value) : rule.value,
            },
            comment:
              'Validation message indicating that the value must be less than or equal to the specified maximum value',
          }),
        };
        break;
    }
  }

  return result;
};

export const mergeConditions = (widget: WidgetModel, adInputField?: EnrichedAdInputField): Condition[] | undefined => {
  const keys = Object.keys(adInputField?.dependencies ?? {});
  const adInputConditions =
    keys.length > 0
      ? keys.map((key) => {
          return {
            field: key,
            operator: 'in',
            value: adInputField?.dependencies?.[key],
          } as Condition;
        })
      : undefined;
  const widgetConditions = widget.conditions;

  // Merge both conditions arrays
  if (adInputConditions && widgetConditions) {
    return [...adInputConditions, ...widgetConditions];
  }

  return adInputConditions ?? widgetConditions;
};

export const mergeValidationRules = (
  widget: WidgetModel,
  adInputField?: EnrichedAdInputField,
  dotNotationName = '',
): RegisterOptions | undefined => {
  const adInputRules =
    !!adInputField && 'rules' in adInputField && adInputField.rules
      ? convertValidationRulesToRHFRules(adInputField.rules, dotNotationName)
      : undefined;
  const widgetRules = 'rules' in widget ? widget.rules : undefined;

  /**
   * If the widget is marked as mandatory in the presentation model and the ad model don't have a MANDATORY rule we
   * make sure to add this rule. This applies to e.g type in the moped ad model
   */
  const mandatoryRule: Pick<RegisterOptions, 'required'> =
    widget.mandatory && !widgetRules?.required
      ? {
          required: i18n.t({
            id: 'mobility-item-insertion.validation.required',
            message: 'This field is required',
            comment: 'Validation message indicating that the field is mandatory',
          }),
        }
      : {};

  return !!adInputRules || !!widgetRules
    ? ({ ...adInputRules, ...widgetRules, ...mandatoryRule } as Pick<
        RegisterOptions,
        'max' | 'min' | 'maxLength' | 'minLength' | 'required'
      >)
    : undefined;
};

/**
 * Splits an array into batches of a specified size.
 *
 * @template T - The type of elements in the array
 * @param array - The array to split into batches
 * @param batchSize - The maximum size of each batch (must be at least 1)
 * @returns An array of batches, where each batch is an array of elements
 *
 * @throws {Error} Throws an error if batchSize is less than or equal to 0
 *
 * @example
 * ```typescript
 * batchArray([1, 2, 3, 4, 5], 2) // Returns [[1, 2], [3, 4], [5]]
 * batchArray(['a', 'b', 'c'], 1) // Returns [['a'], ['b'], ['c']]
 * batchArray([], 3) // Returns []
 * ```
 */
export const batchArray = <T>(array: T[], batchSize: number): T[][] => {
  if (batchSize <= 0) {
    throw Error('Batch size must be at least 1');
  }

  const numberOfBatches = Math.ceil(array.length / batchSize);
  return Array.from({ length: numberOfBatches }).map((_, i) => array.slice(i * batchSize, i * batchSize + batchSize));
};
